<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import { useDocumentAreas } from '@/pages/investors/documents/useDocumentAreas'
import AttachmentController from '@controllers/AttachmentController'
import { useConfirm } from 'primevue/useconfirm'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import FinancingRequestDocumentAreaData = App.Data.Documents.FinancingRequestDocumentAreaData

interface DocumentRow {
  document: AttachmentData
  typeLabel: string
}

interface TypeChoice {
  key: string
  label: string
  area: FinancingRequestDocumentAreaData
  value: string
}

const props = defineProps<{
  areas: FinancingRequestDocumentAreaData[]
}>()

const { t, locale } = useI18n()
const confirm = useConfirm()
const { uploading, upload, remove } = useDocumentUpload()
const { areaLabel } = useDocumentAreas()

const fileInput = ref<HTMLInputElement | null>(null)

const rows = computed<DocumentRow[]>(() =>
  props.areas.flatMap((area) =>
    area.documents.map((document) => ({
      document,
      typeLabel: typeLabel(area, document),
    })),
  ),
)

const typeChoices = computed<TypeChoice[]>(() => {
  const choices = props.areas.flatMap((area) =>
    area.typeOptions.map((type) => ({
      key: `${area.area}:${type.value}`,
      label: type.label,
      area,
      value: type.value,
    })),
  )

  return choices
    .map((choice) => ({ ...choice, label: labelOf(choice, choices) }))
    .sort((one, other) => one.label.localeCompare(other.label, locale.value))
})

const typeOptions = computed(() => typeChoices.value.map((choice) => ({ value: choice.key, label: choice.label })))

const selectedTypeKey = ref<string | null>(null)

const selectedChoice = computed(() => typeChoices.value.find((choice) => choice.key === selectedTypeKey.value) ?? null)

const ready = computed(() => selectedChoice.value !== null && !uploading.value)

function labelOf(choice: TypeChoice, choices: TypeChoice[]): string {
  const ambiguous = choices.filter((other) => other.label === choice.label).length > 1

  return ambiguous ? `${choice.label} (${areaLabel(choice.area.area)})` : choice.label
}

function typeLabel(area: FinancingRequestDocumentAreaData, document: AttachmentData): string {
  return area.typeOptions.find((type) => type.value === document.description)?.label ?? t('unknownType')
}

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement
  const files = [...(input.files ?? [])]
  const choice = selectedChoice.value

  input.value = ''

  if (!choice || !files.length) {
    return
  }

  void upload(
    {
      attachableType: choice.area.attachableType,
      attachableId: choice.area.attachableId,
      documentType: choice.value,
    },
    files,
  )

  selectedTypeKey.value = null
}

function downloadUrl(document: AttachmentData): string | undefined {
  return document.id ? AttachmentController.download(document.id).url : undefined
}

function confirmRemove(document: AttachmentData): void {
  if (!document.id) {
    return
  }

  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { filename: document.filename }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => remove(document.id!),
  })
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-folder-open">
    <ul v-if="rows.length" class="divide-line-2 divide-y">
      <li v-for="row in rows" :key="row.document.id" class="flex flex-wrap items-center gap-3 py-3 first:pt-0">
        <div class="min-w-0 flex-1">
          <p class="text-ink font-medium">{{ row.typeLabel }}</p>
          <a :href="downloadUrl(row.document)" class="text-action block truncate text-sm hover:underline">
            {{ row.document.filename }}
          </a>
        </div>

        <FinestButton
          variant="ghost"
          size="sm"
          icon="pi pi-trash"
          :label="t('actions.remove')"
          @click="confirmRemove(row.document)" />
      </li>
    </ul>

    <p v-else class="text-ink-3 text-sm">{{ t('empty') }}</p>

    <div class="border-line-2 mt-4 flex flex-col gap-3 border-t pt-4">
      <p class="text-ink-2 text-sm font-medium">{{ t('add.heading') }}</p>

      <div>
        <label class="field-label" for="financing-request-document-type">{{ t('add.type') }}</label>
        <FinestSelect
          id="financing-request-document-type"
          v-model="selectedTypeKey"
          :options="typeOptions"
          :placeholder="t('add.typePlaceholder')" />
      </div>

      <FinestButton
        class="self-start"
        variant="secondary"
        icon="pi pi-upload"
        :label="t('add.choose')"
        :disabled="!ready"
        @click="fileInput?.click()" />
    </div>

    <input ref="fileInput" type="file" multiple class="hidden" @change="onSelect" />
  </FinestCard>
</template>

<i18n>
{
  "de": {
    "title": "Dokumente",
    "empty": "Noch keine Dokumente hochgeladen.",
    "unknownType": "Sonstiges Dokument",
    "add": {
      "heading": "Weiteres Dokument hochladen",
      "type": "Was ist die Datei?",
      "typePlaceholder": "Dokumentart wählen",
      "choose": "Datei auswählen"
    },
    "actions": {
      "remove": "Entfernen"
    },
    "delete": {
      "header": "Dokument entfernen",
      "message": "„{filename}“ wird entfernt.",
      "reject": "Abbrechen",
      "accept": "Entfernen"
    }
  },
  "en": {
    "title": "Documents",
    "empty": "No documents uploaded yet.",
    "unknownType": "Other document",
    "add": {
      "heading": "Upload another document",
      "type": "What is the file?",
      "typePlaceholder": "Choose document type",
      "choose": "Choose file"
    },
    "actions": {
      "remove": "Remove"
    },
    "delete": {
      "header": "Remove document",
      "message": "“{filename}” will be removed.",
      "reject": "Cancel",
      "accept": "Remove"
    }
  }
}
</i18n>
